<script setup lang="ts">
	import { computed, ref } from 'vue'

	import { Room } from '../interfaces/hotelDetailsResponse'

	interface Props {
		showSort: boolean
		orderBy: string
		roomsList: Room[]
	}

	interface Emits {
		(event: 'update:orderBy', value: string): void
		(event: 'update:orderByBeds', value: string): void
	}

	const props = defineProps<Props>()
	const emit = defineEmits<Emits>()

	const selectedBedType = ref('all')

	const bedTypeConfig: Record<string, { label: string; icon: string }> = {
		all: {
			label: 'Todas',
			icon: 'M4 6h16M4 12h16M4 18h16',
		},
		D: {
			label: 'Doble',
			icon: 'M2 12h20v6H2v-6zm1-4h18v3H3V8zm0-3a1 1 0 011-1h6a1 1 0 011 1v2H3V5zm10 0a1 1 0 011-1h6a1 1 0 011 1v2h-8V5zm7 13v2h-2v-2H6v2H4v-2H2v2h20v-2h-2z',
		},
		K: {
			label: 'King size',
			icon: 'M1 12h22v6H1v-6zm1-4h20v3H2V8zm1-3a1 1 0 011-1h16a1 1 0 011 1v2H3V5zm18 13v2h-2v-2H5v2H3v-2H1v2h22v-2h-2z',
		},
		P: {
			label: 'Sofá cama',
			icon: 'M3 8a2 2 0 012-2h14a2 2 0 012 2v2H3V8zm-1 3h20v6H2v-6zm0 7h2v2H2v-2zm18 0h2v2h-2v-2z',
		},
		Q: {
			label: 'Queen size',
			icon: 'M2 12h20v6H2v-6zm1-4h18v3H3V8zm1-3a1 1 0 011-1h14a1 1 0 011 1v2H4V5zm16 13v2h-2v-2H6v2H4v-2H2v2h20v-2h-2z',
		},
		S: {
			label: 'Sencilla',
			icon: 'M2 12h20v6H2v-6zm1-4h7v3H3V8zm0-3a1 1 0 011-1h5a1 1 0 011 1v2H3V5zm17 13v2h-2v-2H6v2H4v-2H2v2h20v-2h-2z',
		},
		T: {
			label: 'Twin',
			icon: 'M1 12h9v5H1v-5zm1-3h7v2H2V9zm1-3a1 1 0 011-1h4a1 1 0 011 1v1H3V6zm11 6h9v5h-9v-5zm1-3h7v2h-7V9zm1-3a1 1 0 011-1h4a1 1 0 011 1v1h-6V6zM2 17v2h8v-2H2zm12 0v2h8v-2h-8z',
		},
		W: {
			label: 'Cama de agua',
			icon: 'M2 12h20v6H2v-6zm1-4h18v3H3V8zm1-3a1 1 0 011-1h14a1 1 0 011 1v2H4V5zm16 13v2h-2v-2H6v2H4v-2H2v2h20v-2h-2z',
		},
		double: {
			label: 'Camas dobles',
			icon: 'M2 12h20v6H2v-6zm1-4h18v3H3V8zm0-3a1 1 0 011-1h6a1 1 0 011 1v2H3V5zm10 0a1 1 0 011-1h6a1 1 0 011 1v2h-8V5zm7 13v2h-2v-2H6v2H4v-2H2v2h22v-2h-2z',
		},
	}

	// Orden fijo para los tipos de cama
	const bedTypeOrder = ['S', 'D', 'T', 'Q', 'K', 'P', 'W']
	const rooms = [...props.roomsList]

	const bedTypeOptions = computed(() => {
		const typeBeds = new Set<string>()

		rooms.forEach((room) => {
			if (room.roomType && room.roomType.length >= 3) {
				const bedTypeCode = room.roomType.slice(2, 3)
				const bedAmount = parseInt(room.roomType.slice(1, 2))

				if (bedAmount >= 2) {
					typeBeds.add('double')
				}

				if (bedTypeConfig[bedTypeCode]) {
					typeBeds.add(bedTypeCode)
				}
			}
		})

		const options = [
			{
				value: 'all',
				label: bedTypeConfig.all.label,
				icon: bedTypeConfig.all.icon,
			},
			{
				value: 'double',
				label: bedTypeConfig.double.label,
				icon: bedTypeConfig.double.icon,
			},
		]

		bedTypeOrder.forEach((bedType) => {
			if (typeBeds.has(bedType)) {
				options.push({
					value: bedType,
					label: bedTypeConfig[bedType].label,
					icon: bedTypeConfig[bedType].icon,
				})
			}
		})

		return options
	})

	const selectBedType = (value: string) => {
		selectedBedType.value = value
		emit('update:orderByBeds', selectedBedType.value)
	}

	const companyAgreementFIlter = computed(() => {
		return props.roomsList.find((r) => r.companyAgreement)?.companyName
	})
</script>

<template>
	<div class="flex flex-col gap-3">
		<!-- Header row -->
		<div class="flex flex-wrap items-center justify-between gap-2">
			<div class="flex px-1 text-base font-medium sm:text-2xl text-black">
				Seleccione el tipo de su habitación
			</div>

			<!-- Order by price -->
			<label
				v-if="showSort"
				class="ml-auto flex"
			>
				<select
					:value="orderBy"
					@change="
						$emit('update:orderBy', ($event.target as HTMLInputElement).value)
					"
					class="rounded-md border border-gray-300 bg-white py-1 text-sm transition-colors duration-300 focus:border-gray-500 focus:ring-0"
				>
					<option
						class="order-by"
						value="ascending"
					>
						Menor a mayor precio
					</option>

					<option
						class="order-by"
						value="descending"
					>
						Mayor a menor precio
					</option>

					<option
						class="order-by"
						value="agreementCompany"
						v-if="roomsList.some((r) => r.companyAgreement)"
					>
						Con acuerdo de {{ companyAgreementFIlter }}
					</option>
				</select>
			</label>
		</div>

		<!-- Bed type filter buttons -->
		<div
			v-if="showSort"
			class="flex flex-wrap items-center gap-2"
		>
			<span class="text-sm text-gray-500">Tipo de cama:</span>
			<div class="flex flex-wrap gap-1">
				<button
					v-for="option in bedTypeOptions"
					:key="option.value"
					@click="selectBedType(option.value)"
					:class="[
						'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-200',
						selectedBedType === option.value ?
							'bg-slate-600 text-white shadow-sm'
						:	'bg-gray-100 text-gray-600 hover:bg-gray-200',
					]"
				>
					<svg
						class="h-4 w-4"
						:fill="option.value === 'all' ? 'none' : 'currentColor'"
						viewBox="0 0 24 24"
						:stroke="option.value === 'all' ? 'currentColor' : 'none'"
						:stroke-width="option.value === 'all' ? 2 : 0"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							:d="option.icon"
						/>
					</svg>
					<span>{{ option.label }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<style scoped></style>
